/* Finance UI — refactored out of AppStatic.tsx (behavior unchanged). */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Sparkles, Plus, Pencil, Trash2, Tag, Search, Download, Upload,
  ArrowDownCircle, ArrowUpCircle, ChevronDown, X, User, FolderKanban,
  Wallet, TrendingUp, TrendingDown, Coins, Scale,
} from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/utils";
import { DebtPaymentDashboard } from "./DebtPaymentDashboard";
import { FinanceIntegrityPanel } from "./FinanceIntegrityPanel";
import { useFinanceIntegrity } from "@/lib/finance-integrity";
import { StatementReconcilePanel } from "./StatementReconcilePanel";
import {
  FinancePeriod, FinanceEntry, Customer, Task, Stream,
  finComputeRange, formatMoney, getErrorMessage, todayISO, parseAmount, streamLabel,
  useCurrentStream, useFinanceCategories,
  FIN_PRESETS, FIN_CATEGORY_LABEL,
  exportFinanceCsv, importFinanceCsv, buildMonthlyPnL, exportPnLXlsx,
  CategoryCombobox, SectionHeader, ErrorBox, Input, Select, Textarea, Modal,
  PrimaryBtn, GhostBtn, ConfirmBtn, DateInputVN,
  CustomerDetailModal, FinanceCategoriesModal,
} from "../../AppStatic";

export function FinancePage() {
  const qc = useQueryClient();
  const [preset, setPreset] = useState<FinancePeriod>("month");
  const [section, setSection] = useState<"transactions" | "debt">("transactions");
  const { from, to } = useMemo(() => finComputeRange(preset), [preset]);
  const [filterKind, setFilterKind] = useState<"all" | "income" | "expense">("all");
  const [filterCat, setFilterCat] = useState<string>("__all__");
  const [stream] = useCurrentStream();
  const [kindMenuOpen, setKindMenuOpen] = useState(false);
  const [catMenuOpen, setCatMenuOpen] = useState(false);
  const [catSearch, setCatSearch] = useState("");
  const [panelSearch, setPanelSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(10);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const kindMenuRef = useRef<HTMLDivElement>(null);
  const catMenuRef = useRef<HTMLDivElement>(null);
  const [editing, setEditing] = useState<FinanceEntry | "new" | null>(null);
  const [catModalOpen, setCatModalOpen] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [viewingCustomer, setViewingCustomer] = useState<Customer | null>(null);

  const list = useQuery({
    queryKey: ["finance", from, to, stream],
    queryFn: () => api.get<FinanceEntry[]>("/finance", { from, to, stream }),
  });

  // Tổng quan realtime toàn bộ — invalidate cùng prefix ["finance"] khi mọi mutation chạy xong.
  type FinanceSummary = {
    income_all: number; expense_all: number; balance_all: number;
    deposit_total: number; remaining_total: number;
    current_balance: number | null;
    opening_balance: { configured: boolean; amount: number; updated_at?: string | null };
  };
  const summary = useQuery({
    queryKey: ["finance", "summary"],
    queryFn: () => api.get<FinanceSummary>("/finance/summary"),
  });

  // Map khách + task để hiển thị thông tin trong từng dòng tài chính (cọc / tiền cuối / thủ công).
  const finCustomers = useQuery({ queryKey: ["customers"], queryFn: () => api.get<Customer[]>("/customers") });
  const finCustomerMap = useMemo(() => {
    const m = new Map<string, Customer>();
    for (const c of finCustomers.data ?? []) m.set(c.id, c);
    return m;
  }, [finCustomers.data]);
  const taskIds = useMemo(() => {
    const s = new Set<string>();
    for (const r of list.data ?? []) if (r.task_id) s.add(r.task_id);
    return Array.from(s);
  }, [list.data]);
  const finTaskTitles = useQuery({
    queryKey: ["finance_task_titles", taskIds.sort().join(",")],
    enabled: taskIds.length > 0,
    queryFn: async () => {
      const m = new Map<string, string>();
      // Backend không có endpoint bulk → fetch lẻ, song song.
      await Promise.all(taskIds.map(async (id) => {
        try {
          const t = await api.get<Task>(`/tasks/${encodeURIComponent(id)}`);
          if (t?.title) m.set(id, t.title);
        } catch { /* bỏ qua task bị xóa */ }
      }));
      return m;
    },
  });

  const catQuery = useFinanceCategories(stream);
  const knownCategories = useMemo(() => {
    const fromDb = catQuery.data ?? [];
    const fromData = (list.data ?? []).map((e) => e.category ?? "").filter(Boolean);
    return Array.from(new Set([...fromDb, ...fromData])).filter((c) => c && c !== "khac");
  }, [catQuery.data, list.data]);

  const filtered = useMemo(() => (list.data ?? []).filter((e) =>
    // Guard: chỉ giữ entries đúng dòng (chặn DB cũ thiếu cột revenue_stream làm lẫn dòng).
    ((e.revenue_stream ?? "side") === stream) &&
    (filterKind === "all" || e.kind === filterKind) &&
    (filterCat === "__all__" || (e.category ?? "") === filterCat)
  ), [list.data, filterKind, filterCat, stream]);

  // Reset hiển thị khi đổi bộ lọc
  useEffect(() => { setVisibleCount(10); }, [filterKind, filterCat, stream, preset]);

  // Infinite scroll
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setVisibleCount((v) => Math.min(v + 20, filtered.length));
      }
    }, { rootMargin: "120px" });
    io.observe(el);
    return () => io.disconnect();
  }, [filtered.length]);

  // Đóng popover header bảng khi click ra ngoài
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (kindMenuRef.current && !kindMenuRef.current.contains(e.target as Node)) setKindMenuOpen(false);
      if (catMenuRef.current && !catMenuRef.current.contains(e.target as Node)) setCatMenuOpen(false);
    }
    if (kindMenuOpen || catMenuOpen) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [kindMenuOpen, catMenuOpen]);

  const totals = useMemo(() => {
    let income = 0, expense = 0;
    const byCat: Record<string, { income: number; expense: number }> = {};
    for (const e of filtered) {
      const a = Number(e.amount || 0);
      if (e.kind === "income") income += a; else expense += a;
      const c = e.category ?? "khac";
      if (!byCat[c]) byCat[c] = { income: 0, expense: 0 };
      byCat[c][e.kind === "income" ? "income" : "expense"] += a;
    }
    return { income, expense, profit: income - expense, byCat };
  }, [filtered]);

  const del = useMutation({
    mutationFn: (id: string) => api.del(`/finance/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["finance"] }); qc.invalidateQueries({ queryKey: ["finance_categories"] }); },
  });

  const streamSuffix = stream === "main" ? "chinh" : "phu";
  const periodLabel = FIN_PRESETS.find((p) => p.value === preset)?.label ?? preset;

  function handleExport() {
    const csv = exportFinanceCsv(filtered, finCustomerMap, finTaskTitles.data);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `tai-chinh_${streamSuffix}_${from}_${to}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  const pnlMonths = useMemo(() => buildMonthlyPnL(filtered, from, to), [filtered, from, to]);

  function handleExportPnL() {
    exportPnLXlsx(filtered, from, to, streamSuffix);
  }

  async function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true);
    try {
      const r = await importFinanceCsv(f);
      qc.invalidateQueries({ queryKey: ["finance"] });
      qc.invalidateQueries({ queryKey: ["finance_categories"] });
      const parts = [`thêm ${r.created}`, `cập nhật ${r.updated}`];
      if (r.skipped) parts.push(`bỏ qua ${r.skipped}`);
      let msg = `Nhập CSV xong: ${parts.join(", ")}.`;
      if (r.errors.length) msg += `\n\nLỗi (${r.errors.length}):\n` + r.errors.slice(0, 5).join("\n");
      alert(msg);
    } catch (err) {
      alert(getErrorMessage(err, "Nhập CSV thất bại"));
    } finally {
      setImporting(false);
    }
  }

  const catEntries = useMemo(() => {
    const q = panelSearch.trim().toLowerCase();
    const arr = Object.entries(totals.byCat).sort((a, b) => (b[1].income + b[1].expense) - (a[1].income + a[1].expense));
    if (!q) return arr;
    return arr.filter(([cat]) => (FIN_CATEGORY_LABEL[cat] ?? cat).toLowerCase().includes(q));
  }, [totals.byCat, panelSearch]);

  const visibleRows = filtered.slice(0, visibleCount);

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap gap-1">
        <button
          type="button"
          onClick={() => setSection("transactions")}
          className={`h-8 rounded-md border px-3 text-xs ${section === "transactions" ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:bg-muted"}`}
        >Giao dịch</button>
        <button
          type="button"
          onClick={() => setSection("debt")}
          className={`h-8 rounded-md border px-3 text-xs ${section === "debt" ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:bg-muted"}`}
        >Công nợ &amp; Thanh toán</button>
      </div>

      {section === "debt" ? (
        <DebtPaymentDashboard
          currentStream={stream}
          financeEntries={list.data ?? []}
          customers={finCustomers.data ?? []}
          financeLoading={list.isFetching || finCustomers.isFetching}
          financeError={list.error || finCustomers.error}
          onOpenCustomer={(c) => setViewingCustomer(c)}
        />
      ) : (
      <>
      {/* === Tóm tắt theo dòng (stream-scoped, không gộp main+side) === */}
      <StreamSummaryCards stream={stream} totals={totals} filteredCount={filtered.length} summaryFetching={summary.isFetching} />

      {/* Header: trái = preset thời gian, phải = chọn dòng + xuất + thêm */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1">
          {FIN_PRESETS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setPreset(p.value)}
              className={`h-8 rounded-md border px-3 text-xs ${preset === p.value ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:bg-muted"}`}
            >{p.label}</button>
          ))}
        </div>
        <span className="text-xs text-muted-foreground">{fmtDate(from)} → {fmtDate(to)}</span>
        <div className="flex-1" />
        <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${stream === "main" ? "border-primary/40 bg-primary/10 text-primary" : "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"}`}>
          Đang ghi vào: <b>{streamLabel(stream)}</b>
        </span>
        <GhostBtn onClick={handleExport} disabled={filtered.length === 0}>
          <span className="inline-flex items-center gap-1"><Download className="h-3.5 w-3.5" /> CSV {stream === "main" ? "Chính" : "Phụ"}</span>
        </GhostBtn>
        <GhostBtn onClick={() => importInputRef.current?.click()} disabled={importing}>
          <span className="inline-flex items-center gap-1"><Upload className="h-3.5 w-3.5" /> {importing ? "Đang nhập…" : "Nhập CSV"}</span>
        </GhostBtn>
        <input
          ref={importInputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={handleImportFile}
        />
        <GhostBtn onClick={handleExportPnL} disabled={filtered.length === 0}>
          <span className="inline-flex items-center gap-1"><Download className="h-3.5 w-3.5" /> P/L {stream === "main" ? "Chính" : "Phụ"}</span>
        </GhostBtn>
        <PrimaryBtn onClick={() => setEditing("new")}>
          <span className="inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Thêm giao dịch</span>
        </PrimaryBtn>
        <GhostBtn onClick={() => setCatModalOpen(true)} title="Đổi tên / quản lý danh mục">
          <span className="inline-flex items-center gap-1"><Tag className="h-3.5 w-3.5" /> Danh mục</span>
        </GhostBtn>
      </div>

      {/* Quick add */}
      <QuickAddRow stream={stream} knownCategories={knownCategories} />

      {list.error ? <ErrorBox message={getErrorMessage(list.error, "Không tải được tài chính")} /> : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_280px]">
        <div className="rounded-md border border-border bg-card">
          <SectionHeader title={`Giao dịch · ${stream === "main" ? "Dòng chính" : "Dòng phụ"} (${filtered.length})`} loading={list.isFetching} />
          {filtered.length === 0 && !list.isFetching ? (
            <p className="p-4 text-sm text-muted-foreground">Không có dòng nào trong khoảng này.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">Ngày</th>
                    <th className="px-3 py-2 font-medium">
                      <div ref={kindMenuRef} className="relative inline-block">
                        <button type="button" onClick={() => setKindMenuOpen((o) => !o)} className="inline-flex items-center gap-1 rounded px-1 hover:bg-background/60">
                          Loại{filterKind !== "all" ? <span className="ml-1 rounded bg-primary/20 px-1 text-[10px] text-primary">{filterKind === "income" ? "Thu" : "Chi"}</span> : null}
                          <ChevronDown className="h-3 w-3 opacity-60" />
                        </button>
                        {kindMenuOpen ? (
                          <div className="absolute left-0 z-20 mt-1 w-32 overflow-hidden rounded-md border border-border bg-popover py-1 shadow-lg">
                            {(["all","income","expense"] as const).map((k) => (
                              <button key={k} type="button" onClick={() => { setFilterKind(k); setKindMenuOpen(false); }}
                                className={`flex w-full items-center px-2 py-1.5 text-xs hover:bg-muted ${filterKind === k ? "bg-muted/60" : ""}`}>
                                {k === "all" ? "Tất cả" : k === "income" ? "Chỉ thu" : "Chỉ chi"}
                              </button>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </th>
                    <th className="px-3 py-2 font-medium">Dòng</th>
                    <th className="px-3 py-2 font-medium">
                      <div ref={catMenuRef} className="relative inline-block">
                        <button type="button" onClick={() => setCatMenuOpen((o) => !o)} className="inline-flex items-center gap-1 rounded px-1 hover:bg-background/60">
                          Danh mục
                          {filterCat !== "__all__" ? (
                            <span className="ml-1 inline-flex items-center gap-0.5 rounded bg-primary/20 px-1 text-[10px] text-primary">
                              {FIN_CATEGORY_LABEL[filterCat] ?? filterCat}
                              <X className="h-2.5 w-2.5 cursor-pointer" onClick={(e) => { e.stopPropagation(); setFilterCat("__all__"); }} />
                            </span>
                          ) : null}
                          <ChevronDown className="h-3 w-3 opacity-60" />
                        </button>
                        {catMenuOpen ? (
                          <div className="absolute left-0 z-20 mt-1 w-56 overflow-hidden rounded-md border border-border bg-popover shadow-lg">
                            <div className="border-b border-border p-1.5">
                              <input autoFocus value={catSearch} onChange={(e) => setCatSearch(e.target.value)} placeholder="Tìm danh mục…"
                                className="h-7 w-full rounded border border-border bg-background px-2 text-xs outline-none focus:border-primary" />
                            </div>
                            <div className="max-h-56 overflow-y-auto py-1">
                              <button type="button" onClick={() => { setFilterCat("__all__"); setCatMenuOpen(false); setCatSearch(""); }}
                                className={`flex w-full items-center px-2 py-1.5 text-xs hover:bg-muted ${filterCat === "__all__" ? "bg-muted/60" : ""}`}>
                                Tất cả danh mục
                              </button>
                              {knownCategories
                                .filter((c) => {
                                  const q = catSearch.trim().toLowerCase();
                                  if (!q) return true;
                                  return (FIN_CATEGORY_LABEL[c] ?? c).toLowerCase().includes(q) || c.toLowerCase().includes(q);
                                })
                                .map((c) => (
                                  <button key={c} type="button" onClick={() => { setFilterCat(c); setCatMenuOpen(false); setCatSearch(""); }}
                                    className={`flex w-full items-center px-2 py-1.5 text-xs hover:bg-muted ${filterCat === c ? "bg-muted/60" : ""}`}>
                                    {FIN_CATEGORY_LABEL[c] ?? c}
                                  </button>
                                ))}
                              {knownCategories.length === 0 ? <p className="px-2 py-2 text-xs text-muted-foreground">Chưa có danh mục.</p> : null}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </th>
                    <th className="px-3 py-2 font-medium">Mô tả</th>
                    <th className="px-3 py-2 font-medium">Khách / Đơn</th>
                    <th className="px-3 py-2 font-medium">SL × Đơn giá</th>
                    <th className="px-3 py-2 text-right font-medium">Số tiền</th>
                    <th className="px-3 py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r) => (
                    <tr key={r.id} className="border-t border-border hover:bg-muted/40">
                      <td className="whitespace-nowrap px-3 py-2 align-top text-xs">{r.occurred_on ? fmtDate(r.occurred_on) : "—"}</td>
                      <td className="px-3 py-2 align-top">
                        <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs ${r.kind === "income" ? "bg-emerald-500/15 text-emerald-600" : "bg-rose-500/15 text-destructive"}`}>
                          {r.kind === "income" ? <ArrowDownCircle className="h-3 w-3" /> : <ArrowUpCircle className="h-3 w-3" />}
                          {r.kind === "income" ? "Thu" : "Chi"}
                        </span>
                        {r.auto ? <span className="ml-1 inline-flex items-center gap-0.5 rounded border border-border px-1 text-[10px] text-muted-foreground"><Sparkles className="h-2.5 w-2.5" />auto</span> : null}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] ${r.revenue_stream === "main" ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                          {r.revenue_stream === "main" ? "Chính" : "Phụ"}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-top text-xs">{FIN_CATEGORY_LABEL[r.category ?? ""] ?? r.category ?? "—"}</td>
                      <td className="px-3 py-2 align-top">{r.item_name || r.note || "—"}</td>
                      <td className="px-3 py-2 align-top text-xs">
                        {(() => {
                          const cust = r.customer_id ? finCustomerMap.get(r.customer_id) : null;
                          const taskTitle = r.task_id ? (finTaskTitles.data?.get(r.task_id) ?? null) : null;
                          // Tách prefix "Cọc — " từ item_name để khỏi trùng task title.
                          const cleanTitle = taskTitle || (r.item_name ? r.item_name.replace(/^Cọc\s*[—-]\s*/, "") : null);
                          const rAny = r as any;
                          const payerName: string | null = typeof rAny?.payer_name === "string" && rAny.payer_name.trim() ? rAny.payer_name.trim() : null;
                          const matchStatus: string | null = typeof rAny?.match_status === "string" && rAny.match_status.trim() ? rAny.match_status.trim() : null;
                          if (!cust && !cleanTitle && !payerName) return <span className="text-muted-foreground">—</span>;
                          const cAny = (cust ?? null) as any;
                          const vip = Number(cAny?.vip_score ?? 0);
                          const ctags: string[] = Array.isArray(cAny?.tags) ? cAny.tags.filter(Boolean) : [];
                          const cRealName: string | null = typeof cAny?.real_name === "string" && cAny.real_name.trim() ? cAny.real_name.trim() : null;
                          const cBankNames: string[] = Array.isArray(cAny?.bank_transfer_names)
                            ? cAny.bank_transfer_names.map((s: unknown) => (typeof s === "string" ? s.trim() : "")).filter(Boolean)
                            : [];
                          return (
                            <div className="flex flex-col gap-0.5">
                              {cust ? (
                                <span className="inline-flex items-center gap-1 flex-wrap">
                                  <User className="h-3 w-3 text-muted-foreground" />
                                  <button
                                    type="button"
                                    onClick={() => setViewingCustomer(cust)}
                                    className="font-medium text-primary hover:underline"
                                    title="Xem thông tin khách"
                                  >
                                    {cust.name}
                                  </button>
                                  {cust.phone ? <span className="text-muted-foreground">· {cust.phone}</span> : null}
                                  {vip > 0 ? (
                                    <span className={`text-[10px] px-1.5 py-0 rounded-full font-medium ${vip >= 80 ? "bg-amber-500/15 text-amber-600" : vip >= 50 ? "bg-blue-500/15 text-blue-600" : "bg-muted text-muted-foreground"}`} title={`VIP ${vip}`}>VIP {vip}</span>
                                  ) : null}
                                  {ctags.slice(0, 2).map((t) => (
                                    <span key={t} className="text-[10px] px-1.5 py-0 rounded-full bg-muted text-muted-foreground">{t}</span>
                                  ))}
                                  {ctags.length > 2 ? <span className="text-[10px] text-muted-foreground/70">+{ctags.length - 2}</span> : null}
                                </span>
                              ) : null}
                              {cRealName ? (
                                <span className="text-[11px] italic text-muted-foreground/80 truncate" title={`Tên thật: ${cRealName}`}>Tên thật: {cRealName}</span>
                              ) : null}
                              {cBankNames.length > 0 ? (
                                <span className="inline-flex flex-wrap items-center gap-1">
                                  {cBankNames.slice(0, 3).map((t) => (
                                    <span key={t} className="text-[10px] px-1.5 py-0 rounded bg-amber-500/10 text-amber-600 border border-amber-500/20" title={`Tên CK: ${t}`}>₫ {t}</span>
                                  ))}
                                  {cBankNames.length > 3 ? <span className="text-[10px] text-muted-foreground/70">+{cBankNames.length - 3}</span> : null}
                                </span>
                              ) : null}
                              {payerName ? (
                                <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground" title="Tên trên sao kê">
                                  <span className="opacity-70">₫</span>
                                  <span className="truncate max-w-[200px]">{payerName}</span>
                                  {matchStatus && matchStatus !== "matched" ? (
                                    <span className="text-[10px] px-1.5 py-0 rounded bg-amber-500/10 text-amber-600 border border-amber-500/20">
                                      {matchStatus === "unmatched" ? "chưa match" : matchStatus}
                                    </span>
                                  ) : null}
                                </span>
                              ) : null}
                              {cleanTitle ? (
                                <span className="inline-flex items-center gap-1 text-muted-foreground">
                                  <FolderKanban className="h-3 w-3" />
                                  <span className="truncate max-w-[220px]" title={cleanTitle}>{cleanTitle}</span>
                                </span>
                              ) : null}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2 align-top text-xs">
                        {r.quantity != null && r.unit_price != null ? `${r.quantity} × ${formatMoney(Number(r.unit_price))}` : "—"}
                      </td>
                      <td className={`px-3 py-2 text-right align-top font-mono ${r.kind === "income" ? "text-emerald-600" : "text-destructive"}`}>
                        {r.kind === "income" ? "+" : "−"}{formatMoney(Number(r.amount || 0))}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <div className="flex gap-1">
                          <GhostBtn onClick={() => setEditing(r)}><Pencil className="h-3.5 w-3.5" /></GhostBtn>
                          <ConfirmBtn message="Xóa dòng này?" onConfirm={() => del.mutate(r.id)}><Trash2 className="h-3.5 w-3.5" /></ConfirmBtn>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div ref={sentinelRef} className="flex items-center justify-between border-t border-border px-3 py-2 text-xs text-muted-foreground">
                <span>Hiện {visibleRows.length} / {filtered.length}</span>
                {visibleCount < filtered.length ? (
                  <button type="button" onClick={() => setVisibleCount((v) => Math.min(v + 20, filtered.length))}
                    className="rounded border border-border bg-card px-2 py-1 hover:bg-muted">Tải thêm</button>
                ) : null}
              </div>
            </div>
          )}
        </div>

        {/* Category breakdown */}
        <div className="h-fit rounded-md border border-border bg-card p-3">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-sm font-semibold">Theo danh mục</div>
            <span className="text-[10px] text-muted-foreground">{periodLabel}</span>
          </div>
          <div className="mb-2 relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 opacity-50" />
            <input value={panelSearch} onChange={(e) => setPanelSearch(e.target.value)} placeholder="Tìm danh mục…"
              className="h-7 w-full rounded border border-border bg-background pl-6 pr-2 text-xs outline-none focus:border-primary" />
          </div>
          <div className="space-y-2 text-xs">
            {catEntries.length === 0 ? (
              <p className="text-muted-foreground">Chưa có</p>
            ) : (
              catEntries.map(([cat, v]) => (
                  <div key={cat} className="space-y-0.5">
                    <div className="font-medium">{FIN_CATEGORY_LABEL[cat] ?? cat}</div>
                    <div className="flex justify-between text-emerald-600"><span>Thu</span><span className="font-mono">{formatMoney(v.income)}</span></div>
                    <div className="flex justify-between text-destructive"><span>Chi</span><span className="font-mono">{formatMoney(v.expense)}</span></div>
                  </div>
                ))
            )}
          </div>
        </div>
      </div>

      {/* P/L theo tháng */}
      <div className="rounded-md border border-border bg-card">
        
        <section className="space-y-4">
          <FinanceIntegrityPanel />
          <StatementReconcilePanel />
        </section>

<SectionHeader title={`Báo cáo P/L theo tháng · ${stream === "main" ? "Dòng chính" : "Dòng phụ"} (${pnlMonths.length})`} loading={false} />
        {pnlMonths.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">Không có dữ liệu.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Tháng</th>
                  <th className="px-3 py-2 text-right font-medium">Doanh thu</th>
                  <th className="px-3 py-2 text-right font-medium">Chi phí</th>
                  <th className="px-3 py-2 text-right font-medium">Lợi nhuận</th>
                  <th className="px-3 py-2 text-right font-medium">Biên LN</th>
                  <th className="px-3 py-2 text-right font-medium">Thu chính</th>
                  <th className="px-3 py-2 text-right font-medium">Thu phụ</th>
                </tr>
              </thead>
              <tbody>
                {pnlMonths.map((m) => {
                  const margin = m.income > 0 ? (m.profit / m.income) * 100 : 0;
                  return (
                    <tr key={m.month} className="border-t border-border hover:bg-muted/40">
                      <td className="px-3 py-2 font-mono text-xs">{m.month}</td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-600">{formatMoney(m.income)}</td>
                      <td className="px-3 py-2 text-right font-mono text-destructive">{formatMoney(m.expense)}</td>
                      <td className={`px-3 py-2 text-right font-mono ${m.profit >= 0 ? "text-emerald-600" : "text-destructive"}`}>{formatMoney(m.profit)}</td>
                      <td className={`px-3 py-2 text-right text-xs ${margin >= 0 ? "text-emerald-600" : "text-destructive"}`}>{margin.toFixed(1)}%</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{formatMoney(m.byStreamIncome.main || 0)}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{formatMoney(m.byStreamIncome.side || 0)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>
      )}

      {editing ? (
        <FinanceForm
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["finance"] }); }}
        />
      ) : null}
      {catModalOpen ? <FinanceCategoriesModal stream={stream} onClose={() => setCatModalOpen(false)} /> : null}
      {viewingCustomer ? (
        <CustomerDetailModal customer={viewingCustomer} onClose={() => setViewingCustomer(null)} />
      ) : null}
    </section>
  );
}

/* ------------------ Inline quick-add row ------------------ */
function QuickAddRow({ stream, knownCategories }: { stream: "main" | "side"; knownCategories: string[] }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<"income" | "expense" | null>(null);
  const [date, setDate] = useState(todayISO);
  const [category, setCategory] = useState("vat-lieu");
  const [item, setItem] = useState("");
  const [qty, setQty] = useState("1");
  const [unit, setUnit] = useState("");
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const qtyN = Number(qty.replace(/\D/g, "")) || 0;
  const unitN = parseAmount(unit);
  const amount = qtyN * unitN || unitN;

  const save = useMutation({
    mutationFn: async () => {
      if (!kind) throw new Error("Vui lòng chọn Thu hoặc Chi");
      if (!amount || amount <= 0) throw new Error("Nhập đơn giá");
      return api.post<FinanceEntry>("/finance", {
        kind, amount, category, occurred_on: date, revenue_stream: stream,
        item_name: item || null, quantity: qtyN || null,
        unit_price: unitN || null, note: note || null,
      });
    },
    onSuccess: () => {
      setErr(null); setItem(""); setQty("1"); setUnit(""); setNote("");
      qc.invalidateQueries({ queryKey: ["finance"] });
      qc.invalidateQueries({ queryKey: ["finance_categories"] });
    },
    onError: (e) => setErr(getErrorMessage(e, "Lưu thất bại")),
  });

  const kindBtnClass = (k: "income" | "expense", active: boolean) => {
    if (k === "income") {
      return active
        ? "bg-emerald-500 text-white"
        : "bg-card text-emerald-600 hover:bg-emerald-500/10 border border-emerald-500/30";
    }
    return active
      ? "bg-rose-500 text-white"
      : "bg-card text-rose-600 hover:bg-rose-500/10 border border-rose-500/30";
  };

  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setKind("income")}
          className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium transition ${kindBtnClass("income", kind === "income")}`}>
          <ArrowDownCircle className="h-3.5 w-3.5" /> Thu
        </button>
        <button type="button" onClick={() => setKind("expense")}
          className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium transition ${kindBtnClass("expense", kind === "expense")}`}>
          <ArrowUpCircle className="h-3.5 w-3.5" /> Chi
        </button>
        {kind === null ? (
          <span className="text-xs text-muted-foreground">Chọn Thu hoặc Chi để nhập giao dịch.</span>
        ) : (
          <span className="text-xs text-muted-foreground">Nhập nhanh vào <b>{stream === "main" ? "Dòng chính" : "Dòng phụ"}</b> — hỗ trợ "500k" / "1.5tr"</span>
        )}
      </div>

      {kind === null ? null : (
        <>
          <div className={`mb-2 rounded px-2 py-1 text-xs font-semibold ${kind === "income" ? "bg-emerald-500/10 text-emerald-700" : "bg-rose-500/10 text-rose-700"}`}>
            {kind === "income" ? "Đang nhập khoản THU" : "Đang nhập khoản CHI"}
          </div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-[120px_1fr_70px_140px_160px_1fr_auto]">
            <DateInputVN value={date} onChange={(iso) => setDate(iso)} />
            <Input placeholder="Tên món" value={item} onChange={(e) => setItem(e.target.value)} />
            <Input placeholder="SL" value={qty} onChange={(e) => setQty(e.target.value)} className="text-right font-mono" />
            <Input placeholder="Đơn giá" value={unit} onChange={(e) => setUnit(e.target.value)} className="text-right font-mono" />
            <CategoryCombobox value={category} onChange={setCategory} suggestions={knownCategories} placeholder="Danh mục" />
            <Input placeholder="Ghi chú" value={note} onChange={(e) => setNote(e.target.value)} />
            <PrimaryBtn onClick={() => save.mutate()} disabled={save.isPending || !amount}>
              <span className="inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" />{amount ? formatMoney(amount) : "Lưu"}</span>
            </PrimaryBtn>
          </div>
        </>
      )}
      {err ? <p className="mt-2 text-xs text-destructive">{err}</p> : null}
    </div>
  );
}



function FinanceForm({ initial, onClose, onSaved }: { initial: FinanceEntry | null; onClose: () => void; onSaved: () => void }) {
  // Khi tạo mới: bắt buộc người dùng chọn Thu/Chi trước → tránh nhập nhầm.
  const [kind, setKind] = useState<"income" | "expense" | null>(
    initial ? ((initial.kind as "income" | "expense") || "expense") : null,
  );
  const [amount, setAmount] = useState(initial?.amount != null ? String(initial.amount) : "");
  const [quantity, setQuantity] = useState(initial?.quantity != null ? String(initial.quantity) : "");
  const [unitPrice, setUnitPrice] = useState(initial?.unit_price != null ? String(initial.unit_price) : "");
  const [category, setCategory] = useState(initial?.category && initial.category !== "khac" ? initial.category : "");
  const [itemName, setItemName] = useState(initial?.item_name ?? "");
  const [note, setNote] = useState(initial?.note ?? "");
  const [occurredOn, setOccurredOn] = useState(initial?.occurred_on ?? todayISO());
  const [customerId, setCustomerId] = useState(initial?.customer_id ?? "");
  const [currentStream] = useCurrentStream();
  const revStream: Stream = initial?.revenue_stream === "side" || initial?.revenue_stream === "main"
    ? initial.revenue_stream
    : currentStream;
  const [err, setErr] = useState<string | null>(null);

  const customers = useQuery({ queryKey: ["customers"], queryFn: () => api.get<Customer[]>("/customers") });
  const catQuery = useQuery({
    queryKey: ["finance_categories", revStream],
    queryFn: () => api.get<string[]>("/finance/categories", { stream: revStream }),
  });

  useEffect(() => {
    if (quantity !== "" && unitPrice !== "" && !isNaN(Number(quantity)) && !isNaN(Number(unitPrice))) {
      setAmount(String(Number(quantity) * Number(unitPrice)));
    }
  }, [quantity, unitPrice]);

  const save = useMutation({
    mutationFn: async () => {
      if (!kind) throw new Error("Vui lòng chọn Thu hoặc Chi");
      const body = {
        kind,
        amount: Number(amount || 0),
        quantity: quantity === "" ? null : Number(quantity),
        unit_price: unitPrice === "" ? null : Number(unitPrice),
        category: category || null,
        item_name: itemName || null,
        note: note || null,
        occurred_on: occurredOn || todayISO(),
        customer_id: customerId || null,
        revenue_stream: revStream,
      };
      if (initial) return api.put(`/finance/${initial.id}`, body);
      return api.post("/finance", body);
    },
    onSuccess: onSaved,
    onError: (e) => setErr(getErrorMessage(e, "Lưu thất bại")),
  });

  // Bước 1: chưa chọn Thu/Chi → chỉ render bộ chọn loại to rõ.
  if (kind === null) {
    return (
      <Modal title="Thêm giao dịch" onClose={onClose}>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Chọn loại giao dịch trước khi nhập chi tiết:</p>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setKind("income")}
              className="flex flex-col items-center gap-2 rounded-md border-2 border-emerald-500/40 bg-emerald-500/10 px-4 py-6 text-emerald-700 transition hover:bg-emerald-500/20 dark:text-emerald-400"
            >
              <ArrowDownCircle className="h-8 w-8" />
              <span className="text-base font-semibold">Thu</span>
              <span className="text-xs opacity-70">Khoản nhận về</span>
            </button>
            <button
              type="button"
              onClick={() => setKind("expense")}
              className="flex flex-col items-center gap-2 rounded-md border-2 border-rose-500/40 bg-rose-500/10 px-4 py-6 text-destructive transition hover:bg-rose-500/20"
            >
              <ArrowUpCircle className="h-8 w-8" />
              <span className="text-base font-semibold">Chi</span>
              <span className="text-xs opacity-70">Khoản chi ra</span>
            </button>
          </div>
          <div className="flex justify-end">
            <GhostBtn type="button" onClick={onClose}>Hủy</GhostBtn>
          </div>
        </div>
      </Modal>
    );
  }

  const isIncome = kind === "income";
  const kindLabel = isIncome ? "Thu" : "Chi";

  return (
    <Modal title={initial ? `Sửa giao dịch · ${kindLabel}` : `Thêm giao dịch · ${kindLabel}`} onClose={onClose}>
      <form className="space-y-3" onSubmit={(e) => {
        e.preventDefault();
        if (!amount || isNaN(Number(amount))) { setErr("Số tiền không hợp lệ"); return; }
        setErr(null); save.mutate();
      }}>
        {/* Banner stream */}
        <div className={`rounded-md border px-3 py-2 text-xs ${revStream === "main" ? "border-primary/40 bg-primary/10 text-primary" : "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"}`}>
          Đang ghi vào: <b>{streamLabel(revStream)}</b>
          {initial ? <span className="ml-2 opacity-70">(không đổi được dòng của bản ghi cũ)</span> : null}
        </div>

        {/* Loại — luôn hiển thị rõ, có thể đổi nhanh */}
        <div className="flex items-center gap-2">
          <span className="text-sm">Loại:</span>
          <div className="inline-flex overflow-hidden rounded-md border border-border">
            <button type="button" onClick={() => setKind("income")}
              className={`px-3 py-1.5 text-sm ${isIncome ? "bg-emerald-500/20 text-emerald-600" : "bg-card hover:bg-muted"}`}>
              <span className="inline-flex items-center gap-1"><ArrowDownCircle className="h-3.5 w-3.5" /> Thu</span>
            </button>
            <button type="button" onClick={() => setKind("expense")}
              className={`px-3 py-1.5 text-sm ${!isIncome ? "bg-rose-500/20 text-destructive" : "bg-card hover:bg-muted"}`}>
              <span className="inline-flex items-center gap-1"><ArrowUpCircle className="h-3.5 w-3.5" /> Chi</span>
            </button>
          </div>
        </div>

        {/* Thứ tự: Ngày → Tên mục → SL → Đơn giá → Phân loại → Ghi chú → Khách hàng */}
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">Ngày
            <DateInputVN value={occurredOn ?? ""} onChange={(iso) => setOccurredOn(iso)} className="mt-1" />
          </label>
          <label className="block text-sm">Tên mục
            <Input value={itemName ?? ""} onChange={(e) => setItemName(e.target.value)} className="mt-1" placeholder={isIncome ? "Áo, dịch vụ…" : "Vật liệu, vận chuyển…"} />
          </label>
          <label className="block text-sm">Số lượng
            <Input type="number" min="0" step="0.01" value={quantity} onChange={(e) => setQuantity(e.target.value)} className="mt-1" />
          </label>
          <label className="block text-sm">Đơn giá (VND)
            <Input type="number" min="0" value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} className="mt-1" />
          </label>
          <div className="block text-sm">Phân loại
            <div className="mt-1">
              <CategoryCombobox
                value={category === "khac" ? "" : category}
                onChange={setCategory}
                suggestions={catQuery.data ?? []}
                placeholder="Chọn hoặc gõ phân loại…"
              />
            </div>
          </div>
          <label className="block text-sm">Số tiền (VND) *
            <Input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} required className="mt-1" />
          </label>
        </div>

        <label className="block text-sm">Ghi chú
          <Textarea rows={2} value={note ?? ""} onChange={(e) => setNote(e.target.value)} className="mt-1" />
        </label>
        <label className="block text-sm">Khách hàng (gắn link)
          <Select value={customerId ?? ""} onChange={(e) => setCustomerId(e.target.value)} className="mt-1">
            <option value="">— Không gắn —</option>
            {(customers.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        </label>

        {err ? <ErrorBox message={err} /> : null}
        <div className="flex justify-end gap-2">
          <GhostBtn type="button" onClick={onClose}>Hủy</GhostBtn>
          <PrimaryBtn type="submit" disabled={save.isPending}>{save.isPending ? "Đang lưu…" : "Lưu"}</PrimaryBtn>
        </div>
      </form>
    </Modal>
  );
}

function StreamSummaryCards({
  stream, totals, filteredCount, summaryFetching,
}: {
  stream: "main" | "side";
  totals: { income: number; expense: number; profit: number };
  filteredCount: number;
  summaryFetching: boolean;
}) {
  const integ = useFinanceIntegrity();
  const s = integ.data?.streams?.[stream];
  const streamName = stream === "main" ? "Dòng chính" : "Dòng phụ";
  const ledger = s?.ledger_balance ?? 0;
  const income = s?.income ?? 0;
  const expense = s?.expense ?? 0;
  const receivable = s?.receivable_total ?? 0;
  const reconcile = s?.reconcile_diff;
  const ledgerPositive = ledger >= 0;
  const profit = totals.profit;
  const pnlPositive = profit >= 0;
  const loading = integ.isLoading || !s;
  const fetching = integ.isFetching || summaryFetching;

  return (
    <>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {/* Card A — Số dư theo sổ (stream) */}
        <div className={`relative overflow-hidden rounded-xl border-2 p-5 shadow-sm ${ledgerPositive ? "border-emerald-500/60 bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent" : "border-rose-500/60 bg-gradient-to-br from-rose-500/10 via-rose-500/5 to-transparent"}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className={`grid h-10 w-10 place-items-center rounded-lg ${ledgerPositive ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400" : "bg-rose-500/20 text-rose-600 dark:text-rose-400"}`}>
                <Wallet className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-semibold">Số dư theo sổ · {streamName}</p>
                <p className="text-[11px] text-muted-foreground">Tổng thu (dòng) − tổng chi (dòng)</p>
              </div>
            </div>
            {fetching ? <span className="text-[10px] text-muted-foreground">Đang cập nhật…</span> : null}
          </div>
          <p className={`mt-3 text-3xl font-extrabold tracking-tight md:text-4xl ${loading ? "text-muted-foreground" : (ledgerPositive ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400")}`}>
            {loading ? "…" : formatMoney(ledger)}
          </p>
        </div>

        {/* Card B — Lãi/lỗ kỳ đang xem (stream-filtered via `totals`) */}
        <div className={`relative overflow-hidden rounded-xl border-2 p-5 shadow-sm ${pnlPositive ? "border-emerald-500/60 bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent" : "border-rose-500/60 bg-gradient-to-br from-rose-500/10 via-rose-500/5 to-transparent"}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className={`grid h-10 w-10 place-items-center rounded-lg ${pnlPositive ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400" : "bg-rose-500/20 text-rose-600 dark:text-rose-400"}`}>
                {pnlPositive ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
              </div>
              <div>
                <p className="text-sm font-semibold">Lãi/lỗ kỳ đang xem · {streamName}</p>
                <p className="text-[11px] text-muted-foreground">Doanh thu kỳ − chi phí kỳ (đã lọc theo dòng)</p>
              </div>
            </div>
          </div>
          <p className={`mt-3 text-3xl font-extrabold tracking-tight md:text-4xl ${pnlPositive ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>
            {formatMoney(profit)}
          </p>
        </div>
      </div>

      {/* Card C + D — Còn phải thu / Chênh lệch đối soát (per stream) */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-lg border border-amber-500/50 bg-amber-500/5 p-4">
          <div className="flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-md bg-amber-500/20 text-amber-600 dark:text-amber-400"><Coins className="h-4 w-4" /></div>
            <p className="text-xs font-semibold text-amber-700 dark:text-amber-400">Còn phải thu · {streamName}</p>
          </div>
          <p className="mt-2 text-2xl font-bold text-amber-700 dark:text-amber-400">{loading ? "…" : formatMoney(receivable)}</p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">Task chưa tất toán: total_amount − deposit_amount</p>
        </div>
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-md bg-muted text-muted-foreground"><Scale className="h-4 w-4" /></div>
            <p className="text-xs font-semibold">Chênh lệch đối soát · {streamName}</p>
          </div>
          <p className="mt-2 text-2xl font-bold">
            {loading ? "…" : (reconcile == null ? <span className="text-muted-foreground text-base font-medium">Chưa có dữ liệu Cake</span> : formatMoney(reconcile))}
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">So với statement đã khớp (chỉ tham chiếu, không cộng vào sổ)</p>
        </div>
      </div>

      {/* Chỉ số phụ — compact, vẫn theo dòng */}
      <div className="rounded-md border border-border bg-card p-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="text-xs font-medium text-muted-foreground">Chỉ số phụ · {streamName}</p>
          <span className="text-[10px] text-muted-foreground">{fetching ? "Đang cập nhật…" : "Realtime"}</span>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
          <div>
            <p className="text-[11px] text-muted-foreground">Doanh thu kỳ đang xem</p>
            <p className="mt-0.5 text-sm font-semibold text-emerald-600">{formatMoney(totals.income)}</p>
          </div>
          <div>
            <p className="text-[11px] text-muted-foreground">Chi phí kỳ đang xem</p>
            <p className="mt-0.5 text-sm font-semibold text-destructive">{formatMoney(totals.expense)}</p>
          </div>
          <div>
            <p className="text-[11px] text-muted-foreground">Số giao dịch kỳ đang xem</p>
            <p className="mt-0.5 text-sm font-semibold">{filteredCount}</p>
          </div>
          <div>
            <p className="text-[11px] text-muted-foreground">Tổng thu {streamName}</p>
            <p className="mt-0.5 text-sm font-semibold text-emerald-600">{loading ? "…" : formatMoney(income)}</p>
          </div>
          <div>
            <p className="text-[11px] text-muted-foreground">Tổng chi {streamName}</p>
            <p className="mt-0.5 text-sm font-semibold text-destructive">{loading ? "…" : formatMoney(expense)}</p>
          </div>
        </div>
      </div>
    </>
  );
}
